/**
 * DOS518 - Digital Operations System for Wildfire Management
 * 
 * Copyright (c) 2024-2025 Angelo Irtilaç
 * All rights reserved.
 * 
 * This software is proprietary and confidential.
 * Unauthorized copying, distribution, modification, or use is strictly prohibited.
 * 
 * ---
 * 
 * Author: Angelo Irtilaç
 * Qualifications: DOS (Direttore delle Operazioni di Spegnimento)
 *                39 years - Corpo Nazionale dei Vigili del Fuoco
 *                DOS Trainer & Instructor
 * 
 * Location: Carpi, Italy
 * Contact: [inserire email]
 * 
 * ---
 * 
 * This software implements official Italian Fire Department protocols:
 * - C.O.A.U. (Centro Operativo Aereo Unificato)
 * - SI.TA.C. (Sistema Tattico di Coordinamento)
 * - L.A.C.E.S. (Lookout, Awareness, Communication, Escape routes, Safety zones)
 * 
 * Developed with operational expertise acquired through decades of wildfire
 * emergency management and coordination of suppression operations.
 * 
 * For commercial licensing inquiries, please contact the author.
 */
// AvvicinamentoModal - CON LOG INTEGRATO E CALLSIGN DINAMICO
import React, { useState, useEffect } from 'react';
import { useAppStore } from '../../store/useAppStore';
import { toPhonetic, toPhoneticCallsign } from '../../utils/fraseologia';
import './AvvicinamentoModal.css';

interface AvvicinamentoModalProps {
  isOpen: boolean;
  onClose: () => void;
}

export const AvvicinamentoModal: React.FC<AvvicinamentoModalProps> = ({ isOpen, onClose }) => {
  const addLogComunicazione = useAppStore(state => state.addLogComunicazione);
  const mezziAerei = useAppStore(state => state.mezziAerei);

  const coau = useAppStore(state => state.coau);

  const [mezzoSelezionatoId, setMezzoSelezionatoId] = useState<string>('');
  const [qualitaRicezione, setQualitaRicezione] = useState<number | null>(null);
  const [mostraRipetizione, setMostraRipetizione] = useState(false);

  // Auto-seleziona il primo mezzo in avvicinamento all'apertura
  useEffect(() => {
    if (isOpen) {
      const inAvvicinamento = mezziAerei.find(
        m => m.posizioneOperativa?.toLowerCase().replace(/\s+/g, '_') === 'in_avvicinamento'
      );
      if (inAvvicinamento) {
        setMezzoSelezionatoId(inAvvicinamento.id);
      }
      setQualitaRicezione(null);
      setMostraRipetizione(false);
    }
  }, [isOpen]);

  if (!isOpen) return null;

  // Callsign dinamico: preferisce il mezzo selezionato, poi il primo in avvicinamento, poi il primo disponibile

  const mezzoAttivo =
    mezziAerei.find(m => m.id === mezzoSelezionatoId) ||
    mezziAerei.find(m => m.posizioneOperativa?.toLowerCase().replace(/\s+/g, '_') === 'in_avvicinamento') ||
    mezziAerei[0];

  const callsignMezzo = mezzoAttivo ? toPhoneticCallsign(mezzoAttivo.sigla) : 'STAZIONE CHIAMANTE';
  const siglaMezzo = mezzoAttivo?.sigla || 'MEZZO';

  // Nominativo DOS da COAU → convertito in fonetico ICAO
  const nominativoDOS = (coau as any)?.nominativoDOS;
  const callsignDOS = nominativoDOS ? toPhonetic(nominativoDOS) : 'DOS';

  const handleSelectQualita = (value: number) => {
    setQualitaRicezione(value);
    addLogComunicazione({
      tipo: 'avvicinamento',
      mittente: nominativoDOS || 'DOS',
      destinatario: siglaMezzo,
      messaggio: `DOS: ${callsignMezzo} ${callsignDOS} vi ricevo ${value}/5 | AEREO: ${callsignDOS} ${callsignMezzo} (${value}/5) anche per noi. Pronto a copiare i dati Sicurezza`,
      priorita: 'normale',
    });
  };

  const handleRichiestaRipetizione = () => {
    setMostraRipetizione(true);
    addLogComunicazione({
      tipo: 'avvicinamento',
      mittente: nominativoDOS || 'DOS',
      destinatario: 'Stazione Chiamante',
      messaggio: 'Stazione Chiamante ripetete',
      priorita: 'normale',
    });
  };

  const qualitaOptions = [
    { value: 1, label: '1/5 - Incomprensibile', color: '#ed8936' },
    { value: 2, label: '2/5 - A tratti', color: '#ed8936' },
    { value: 3, label: '3/5 - Con difficoltà', color: '#ff6b35' },
    { value: 4, label: '4/5 - Comprensibile', color: '#48bb78' },
    { value: 5, label: '5/5 - Perfettamente Comprensibile', color: '#48bb78' },
  ];

  return (
    <div className="avvicinamento-modal-overlay" onClick={onClose}>
      <div className="avvicinamento-modal-content" onClick={(e) => e.stopPropagation()}>
        <div className="avvicinamento-modal-header">
          <h3>🛬 Contatto Radio - Avvicinamento all'incendio</h3>
          <button className="btn-close" onClick={onClose}>✕</button>
        </div>

        <div className="avvicinamento-modal-body">

          {/* SELEZIONE MEZZO AEREO */}
          {mezziAerei.length > 0 ? (
            <div className="comm-step active" style={{ marginBottom: '15px' }}>
              <div className="step-header">
                <div className="step-number">✈️</div>
                <h4>Aereo in Arrivo</h4>
              </div>
              <div className="step-content">
                <select
                  value={mezzoSelezionatoId}
                  onChange={e => {
                    setMezzoSelezionatoId(e.target.value);
                    setQualitaRicezione(null);
                    setMostraRipetizione(false);
                  }}
                  style={{
                    width: '100%', padding: '10px', background: 'rgba(255,255,255,0.1)',
                    border: '2px solid rgba(255,107,53,0.5)', borderRadius: '8px',
                    color: '#fff', fontSize: '14px',
                  }}
                >
                  <option value="">— Seleziona mezzo (o usa il primo in avvicinamento) —</option>
                  {mezziAerei.map(m => (
                    <option key={m.id} value={m.id}>
                      {m.sigla} — {m.tipo === 'ala_fissa' ? 'Canadair' : 'Elicottero'} ({m.posizioneOperativa})
                    </option>
                  ))}
                </select>
                <div style={{ marginTop: '8px', fontSize: '13px', display: 'flex', flexDirection: 'column', gap: '4px' }}>
                  {mezzoAttivo && (
                    <span style={{ color: '#ff6b35', fontWeight: 700 }}>Mezzo fonetico: <em>{callsignMezzo}</em></span>
                  )}
                  <span style={{ color: nominativoDOS ? '#2ecc71' : '#aaa', fontWeight: 700 }}>
                    DOS fonetico: <em>{callsignDOS}</em>
                    {!nominativoDOS && <span style={{ color: '#ffc107', fontWeight: 400 }}> — inserisci il Nominativo DOS in COAU → Personale e Mezzi</span>}
                  </span>
                </div>
              </div>
            </div>
          ) : (
            <div style={{ padding: '12px', background: 'rgba(255,193,7,0.1)', border: '2px solid #ffc107', borderRadius: '8px', marginBottom: '15px' }}>
              <p style={{ color: '#ffc107', margin: 0, fontSize: '13px' }}>
                ⚠️ Nessun mezzo aereo registrato. Vai in <strong>Risorse → Mezzi Aerei</strong> per aggiungerne uno.
              </p>
            </div>
          )}

          {/* PASSO 1: Messaggio Aereo */}
          <div className="comm-step active">
            <div className="step-header">
              <div className="step-number">1</div>
              <h4>Chiamata Iniziale Aeromobile</h4>
            </div>
            <div className="step-content">
              <div className="radio-message aereo">
                <div className="message-label">🛩️ AEROMOBILE</div>
                <div className="message-text">
                  <strong>{callsignDOS} {callsignMezzo}</strong>{' '}
                  in arrivo da…. 8 primi al target
                </div>
              </div>
            </div>
          </div>

          {/* PASSO 2: DOS Risponde */}
          <div className="comm-step active">
            <div className="step-header">
              <div className="step-number">2</div>
              <h4>DOS Risponde - Qualità Ricezione</h4>
            </div>
            <div className="step-content">
              <div className="radio-message dos">
                <div className="message-label">📡 DOS</div>
                <div className="message-text">
                  <strong>{callsignMezzo} {callsignDOS}</strong>, vi ricevo:
                </div>
              </div>

              <div className="input-section">
                <h5 className="section-title">📡 Qualità Ricezione</h5>
                <div className="qualita-buttons">
                  {qualitaOptions.map((option) => (
                    <button
                      key={option.value}
                      className={`option-btn ${qualitaRicezione === option.value ? 'selected' : ''} ${option.value <= 2 ? 'warning' : ''}`}
                      onClick={() => handleSelectQualita(option.value)}
                      style={qualitaRicezione === option.value ? { borderColor: option.color } : {}}
                    >
                      {option.label}
                    </button>
                  ))}
                </div>

                {qualitaRicezione !== null && (
                  <>
                    <div className="radio-message dos" style={{ marginBottom: '10px' }}>
                      <div className="message-label">📡 DOS</div>
                      <div className="message-text">
                        <strong>{callsignMezzo} {callsignDOS}</strong> vi ricevo {qualitaRicezione}/5
                      </div>
                    </div>
                    <div className="radio-message aereo" style={{ marginBottom: '20px' }}>
                      <div className="message-label">🛩️ AEROMOBILE</div>
                      <div className="message-text">
                        <strong>{callsignDOS} {callsignMezzo}</strong> ({qualitaRicezione}/5) anche per noi. Pronto a copiare i dati Sicurezza
                      </div>
                    </div>
                    <div style={{ marginTop: '15px', padding: '12px', background: 'rgba(46,204,113,0.1)', borderRadius: '8px', border: '2px solid #2ecc71' }}>
                      <p style={{ color: '#2ecc71', margin: 0, fontSize: '13px', fontWeight: '700' }}>✅ Comunicazione registrata nel Log!</p>
                    </div>
                  </>
                )}

                <div className="step-actions">
                  <button
                    className="btn btn-warning"
                    onClick={handleRichiestaRipetizione}
                    disabled={qualitaRicezione === null}
                  >
                    🔁 Richiedi Ripetizione
                  </button>
                </div>

                {mostraRipetizione && (
                  <>
                    <div className="radio-message dos" style={{ marginTop: '20px' }}>
                      <div className="message-label">📡 DOS</div>
                      <div className="message-text">
                        <strong>Stazione Chiamante ripetete</strong>
                      </div>
                    </div>
                    <div style={{ marginTop: '10px', padding: '12px', background: 'rgba(46,204,113,0.1)', borderRadius: '8px', border: '2px solid #2ecc71' }}>
                      <p style={{ color: '#2ecc71', margin: 0, fontSize: '13px', fontWeight: '700' }}>✅ Richiesta ripetizione registrata!</p>
                    </div>
                  </>
                )}
              </div>
            </div>
          </div>

          <div className="modal-footer-actions">
            <button className="btn btn-primary" onClick={onClose}>✅ Chiudi</button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AvvicinamentoModal;
